/**
 * Trade economics (acquisitions / postclose only).
 *
 * The trade convention is derived from the loan product by the backend
 * (app/models/acquisition_economics.py):
 *   - DSCR products  -> "Bond Points"     (price in bond points, par = 100)
 *   - RTL/GUC/bridge -> "Buy Rate + Strip" (purchased at par, value is the strip)
 *
 * GET /economics/{dealId} recomputes from stored inputs and is READ-ONLY. The two
 * POST routes compute AND persist onto the loan, and return the full updated deal
 * with an `economics` key — so a successful recompute refreshes the drilldown.
 *
 * Structure per the Drilldown Tab Header Standard: page header + KPI strip on the
 * page background, then two peer `LoanSectionCard`s. "Recompute & save" is the
 * tab's ONE primary action and lives in the page header — it persists onto the
 * loan, which is a page-level write, not a card-scoped view change. Reset is a
 * card-scoped control and stays in the recompute card.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import CalculateOutlined from "@mui/icons-material/CalculateOutlined";
import TrendingUpOutlined from "@mui/icons-material/TrendingUpOutlined";
import TuneOutlined from "@mui/icons-material/TuneOutlined";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import UpbOverview from "./UpbOverview";
import { InputRow, StatRow } from "./economicsRows";
import EconomicsEditorCard, {
  editedKeys,
  editorPayload,
  editorSeed,
} from "./EconomicsEditorCard";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import LoanSectionCard from "../../shared/tabLayout/LoanSectionCard";
import LoanTabShell from "../../shared/tabLayout/LoanTabShell";
import SectionEmptyState from "../../shared/tabLayout/SectionEmptyState";
import { TabGhostButton } from "../../shared/tabLayout/tabButtons";
import { TAB_TOKENS as T } from "../../shared/tabLayout/tabTokens";
import { losAcquisitionsApi } from "../../../../api/losAcquisitionsApi";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import { formatPercent } from "../../../../utils/numbers/formatPercent";

const BOND_POINTS = "Bond Points";
const BUY_RATE_STRIP = "Buy Rate + Strip";

/**
 * Which layout the tab renders — kept switchable so either can come back:
 *   "single" one editable card: stored inputs prefilled + results beside them
 *            (EconomicsEditorCard.js). Every save sends all prefilled inputs.
 *   "split"  the original two cards: read-only Current economics + a blank
 *            Recompute inputs card that sends only the fields typed in.
 * To revert, set this to "split" — nothing else changes.
 */
const ECONOMICS_LAYOUT = "split";

// Rates come off the wire as decimals (0.0825) — formatPercent expects that.
// All three return null for missing input so the standard's em-dash rule (in the
// KPI tile / StatRow) owns how "no data" looks, rather than each formatter
// inventing its own placeholder.
const pct = (v) => (v == null ? null : formatPercent(v));
const money = (v) => (v == null ? null : formatDollar(v, null));
const points = (v) => (v == null ? null : `${Number(v).toFixed(3)}`);

// What to show per convention. `economics` keys are exactly the dict the
// backend's rtl_buy_rate_economics / dscr_bond_points_economics return.
const ROWS = {
  [BUY_RATE_STRIP]: [
    { key: "couponRate", label: "Coupon rate", fmt: pct, group: "Pricing" },
    { key: "buyRate", label: "Buy rate", fmt: pct, group: "Pricing" },
    {
      key: "interestStripRate",
      label: "Interest strip rate",
      fmt: pct,
      group: "Pricing",
    },
    {
      key: "interestStripAnnualAmount",
      label: "Strip / year",
      fmt: money,
      group: "Amounts",
    },
    {
      key: "interestStripValue",
      label: "Strip value (term)",
      fmt: money,
      group: "Amounts",
    },
    {
      key: "interestStripTermMonths",
      label: "Strip term",
      fmt: (v) => (v == null ? null : `${v} mo`),
      group: "Amounts",
    },
    { key: "currentUPB", label: "Current UPB", fmt: money, group: "Amounts" },
    {
      key: "purchasePriceAmount",
      label: "Purchase price (at par)",
      fmt: money,
      group: "Amounts",
    },
    {
      key: "accruedInterest",
      label: "Accrued interest",
      fmt: money,
      group: "Amounts",
    },
  ],
  [BOND_POINTS]: [
    { key: "couponRate", label: "Coupon rate", fmt: pct, group: "Pricing" },
    {
      key: "bondPrice",
      label: "Bond price (par = 100)",
      fmt: points,
      group: "Pricing",
    },
    {
      key: "bidPricePercent",
      label: "Bid price %",
      fmt: points,
      group: "Pricing",
    },
    {
      key: "premiumDiscountPoints",
      label: "Premium / discount points",
      fmt: points,
      group: "Pricing",
    },
    {
      key: "premiumDiscount",
      label: "Premium / discount",
      fmt: money,
      group: "Amounts",
    },
    { key: "currentUPB", label: "Current UPB", fmt: money, group: "Amounts" },
    {
      key: "purchasePriceAmount",
      label: "Purchase price",
      fmt: money,
      group: "Amounts",
    },
    {
      key: "accruedInterest",
      label: "Accrued interest",
      fmt: money,
      group: "Amounts",
    },
  ],
};

// Recompute inputs, per convention. Keys match the POST body the router reads.
const INPUTS = {
  [BUY_RATE_STRIP]: [
    { key: "coupon_rate", label: "Coupon rate", help: "Decimal, e.g. 0.0925" },
    { key: "override_buy_rate", label: "Buy rate (override)", help: "Decimal" },
    {
      key: "pricing_base_rate",
      label: "Pricing base rate",
      help: "Or use with spread",
    },
    { key: "pricing_spread", label: "Pricing spread" },
    { key: "risk_spread_adjustment", label: "Risk spread adj." },
    { key: "term_months", label: "Strip term (months)" },
    { key: "accrued_interest", label: "Accrued interest", money: true },
    { key: "currentUPB", label: "Current UPB", money: true },
  ],
  [BOND_POINTS]: [
    { key: "bondPrice", label: "Bond price", help: "Par = 100" },
    { key: "rate_at_lock", label: "Rate at lock", help: "Decimal" },
    { key: "margin_points_at_lock", label: "Margin points at lock" },
    { key: "accrued_interest", label: "Accrued interest", money: true },
    { key: "currentUPB", label: "Current UPB", money: true },
  ],
};

const AcquisitionsEconomicsTab = ({ value, tab }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const loan = useSelector((state) => state.loanDrilldown);
  const dealId = loan?._id;

  const [economics, setEconomics] = useState(null);
  const [convention, setConvention] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const res = await losAcquisitionsApi.getEconomics(dealId);
      setEconomics(res?.economics || null);
      setConvention(res?.tradeConvention || null);
    } catch (e) {
      enqueueSnackbar("Could not load trade economics", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [dealId, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const isBond = convention === BOND_POINTS;
  const rows = useMemo(() => ROWS[convention] || [], [convention]);
  const inputs = useMemo(() => INPUTS[convention] || [], [convention]);
  const single = ECONOMICS_LAYOUT === "single";
  // "single": the saved value of every stored input, which the card prefills
  // and the save always sends (see EconomicsEditorCard.js).
  const seed = useMemo(
    () => editorSeed(convention, economics, loan),
    [convention, economics, loan],
  );
  const dirtyCount = useMemo(
    () =>
      single
        ? editedKeys(seed, form).length
        : Object.values(form).filter((v) => v !== "" && v != null).length,
    [single, seed, form],
  );

  const handleRecompute = useCallback(async () => {
    if (!dealId) return;
    // "single" sends every prefilled input (the routes don't fall back to the
    // stored buy rate / strip term). "split" sends only what was typed and
    // lets the backend fall back for the rest.
    const payload = single
      ? editorPayload(convention, seed, form)
      : Object.entries(form).reduce((acc, [k, v]) => {
          if (v === "" || v == null) return acc;
          const n = Number(v);
          return { ...acc, [k]: Number.isNaN(n) ? v : n };
        }, {});

    setSaving(true);
    try {
      const updated = isBond
        ? await losAcquisitionsApi.setDscrBondPoints(dealId, payload)
        : await losAcquisitionsApi.setRtlBuyRate(dealId, payload);
      // These routes persist and return the full deal + an `economics` key.
      setEconomics(updated?.economics || null);
      if (updated?._id) dispatch(loanDrilldownSet(updated));
      setForm({});
      enqueueSnackbar("Trade economics recomputed", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Could not recompute economics", { variant: "error" });
    } finally {
      setSaving(false);
    }
  }, [
    dealId,
    form,
    isBond,
    single,
    convention,
    seed,
    dispatch,
    enqueueSnackbar,
  ]);

  /* ── KPI strip ──
     The four figures the trade desk actually reads off this tab. No tones: none
     of these require action, they're just the numbers. */
  const kpis = useMemo(() => {
    if (!convention) return [];
    return [
      {
        key: "wire",
        label: "Funding wire amount",
        value: money(economics?.fundingWireAmount),
      },
      {
        key: "price",
        label: "Purchase price",
        value: money(economics?.purchasePriceAmount),
      },
      // Current UPB leads the UPB overview below, so the strip carries
      // accrued interest instead of repeating it.
      {
        key: "accrued",
        label: "Accrued interest",
        value: money(economics?.accruedInterest),
      },
      {
        key: "carry",
        label: isBond ? "Premium / discount" : "Strip value (term)",
        value: money(
          isBond ? economics?.premiumDiscount : economics?.interestStripValue,
        ),
      },
    ];
  }, [convention, economics, isBond]);

  return (
    <LoanDrilldownTabPanel value={value} tab={tab}>
      <LoanTabShell
        title="Trade economics"
        subTitle="What this loan costs to buy and what it earns, computed from the trade convention its product implies."
        meta={
          convention ? (
            <Chip
              size="small"
              label={convention}
              sx={{
                height: 21,
                fontSize: 11,
                fontWeight: 600,
                background: isBond ? T.color.brandSoft : T.color.okSoft,
                color: isBond ? T.color.brand : T.color.okInk,
              }}
            />
          ) : null
        }
        // The tab's one primary action — it persists the recomputed economics
        // onto the loan. Hidden entirely when there's no convention to compute
        // against, rather than rendered disabled.
        primaryAction={
          inputs.length > 0
            ? {
                label: "Recompute & save",
                icon: <CalculateOutlined />,
                loading: saving,
                onClick: handleRecompute,
              }
            : null
        }
        kpis={kpis}
      >
        {loading ? (
          <LoanSectionCard
            title="Current economics"
            icon={<TrendingUpOutlined />}
          >
            <Flx center sx={{ paddingBlock: "40px" }}>
              <CircularProgress size={22} />
            </Flx>
          </LoanSectionCard>
        ) : (
          <Flx column sx={{ gap: "24px" }}>
            {single ? (
              <EconomicsEditorCard
                convention={convention}
                economics={economics}
                seed={seed}
                edits={form}
                onEdit={(k, v) => setForm((p) => ({ ...p, [k]: v }))}
                onReset={() => setForm({})}
                saving={saving}
              />
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: inputs.length > 0 ? "1.15fr 1fr" : "1fr",
                  gap: "24px",
                  alignItems: "start",
                  "@media (max-width: 960px)": { gridTemplateColumns: "1fr" },
                }}
              >
                {/* ── Computed economics ── */}
                <LoanSectionCard
                  icon={<TrendingUpOutlined />}
                  title="Current economics"
                  subTitle={
                    convention
                      ? `Recomputed from the loan's stored inputs under the ${convention} convention. Read-only.`
                      : undefined
                  }
                >
                  {rows.length === 0 ? (
                    <SectionEmptyState
                      icon={<TrendingUpOutlined />}
                      message="Set the loan's product type to resolve a trade convention and compute its economics."
                    />
                  ) : (
                    <>
                      <Box>
                        {rows.map((r, i) => {
                          const newGroup =
                            r.group &&
                            (i === 0 || rows[i - 1].group !== r.group);
                          return (
                            <Fragment key={r.key}>
                              {newGroup && (
                                // Each group opens with clear air above it, so
                                // Pricing and Amounts read as separate blocks.
                                <Txt
                                  component="div"
                                  sx={{
                                    ...T.type.fieldLabel,
                                    fontSize: "12.5px",
                                    color: T.color.ink,
                                    paddingTop: i === 0 ? "2px" : "28px",
                                    paddingBottom: 0,
                                  }}
                                >
                                  {r.group}
                                </Txt>
                              )}
                              <StatRow
                                label={r.label}
                                value={r.fmt(economics?.[r.key])}
                              />
                            </Fragment>
                          );
                        })}
                      </Box>

                      {/* The bottom line the trade desk wires. */}
                      <Flx
                        ac
                        jb
                        sx={{
                          marginTop: "20px",
                          padding: "12px 16px",
                          gap: "14px",
                          background: T.color.brandSoft,
                          border: "1px solid #d9e0fa",
                          borderRadius: T.radius.sm,
                        }}
                      >
                        <Flx column sx={{ gap: "1px", minWidth: 0 }}>
                          <Txt
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: T.color.accentInk,
                            }}
                          >
                            Funding wire amount
                          </Txt>
                          <Txt
                            sx={{
                              fontSize: 10.5,
                              fontWeight: 500,
                              color: T.color.accentInk,
                              opacity: 0.8,
                            }}
                          >
                            Purchase price + accrued interest
                          </Txt>
                        </Flx>
                        <Txt
                          sx={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: T.color.accentInk,
                            fontVariantNumeric: "tabular-nums",
                            flexShrink: 0,
                          }}
                        >
                          {money(economics?.fundingWireAmount) || "—"}
                        </Txt>
                      </Flx>
                    </>
                  )}
                </LoanSectionCard>

                {/* ── Recompute inputs ──
                Reset is card-scoped (it clears these fields), so it stays here.
                The save itself is the page header's primary action. */}
                {inputs.length > 0 && (
                  <LoanSectionCard
                    icon={<TuneOutlined />}
                    title="Recompute inputs"
                    subTitle="Leave a field blank to keep the loan's stored value."
                    bodySx={{ paddingBlock: "20px" }}
                    count={
                      dirtyCount > 0 ? `${dirtyCount} overridden` : undefined
                    }
                    controls={
                      <TabGhostButton
                        size="sm"
                        label="Reset"
                        disabled={saving || dirtyCount === 0}
                        onClick={() => setForm({})}
                      />
                    }
                  >
                    {/* One input per row, label left / control right — the
                    same row the Loan Info tab edits its fields in. */}
                    <Box>
                      {inputs.map((f) => (
                        <InputRow
                          key={f.key}
                          field={f}
                          value={form[f.key]}
                          onChange={(v) =>
                            setForm((p) => ({ ...p, [f.key]: v }))
                          }
                        />
                      ))}
                    </Box>
                    <Txt
                      component="div"
                      sx={{
                        ...T.type.meta,
                        fontSize: "12px",
                        marginTop: "16px",
                        lineHeight: 1.5,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ fontWeight: 600, color: T.color.ink2 }}
                      >
                        Recompute &amp; save
                      </Box>{" "}
                      (top right of the page) computes and persists these onto
                      the loan, then refreshes the drilldown.
                    </Txt>
                  </LoanSectionCard>
                )}
              </Box>
            )}
            {/* The purchased balance — below the economics it feeds. */}
            <UpbOverview loan={loan} />
          </Flx>
        )}
      </LoanTabShell>
    </LoanDrilldownTabPanel>
  );
};

export default AcquisitionsEconomicsTab;
